import { useState, useEffect } from "react";
import { GradientHeading } from "@/components/ui/gradient-heading";
import { CompanyLogo } from "@/components/CompanyLogo";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import clipperLogo from "@/assets/clipper-logo.png";
import esplanadeLogo from "@/assets/esplanade-logo.png";
import centerparcsLogo from "@/assets/centerparcs-logo.png";
import ambienteLogo from "@/assets/ambiente-logo.png";
import deimannLogo from "@/assets/deimann-logo.svg";
import jagdhausEidenLogo from "@/assets/jagdhaus-eiden-logo.svg";
import strandhotelDuenenmeerLogo from "@/assets/strandhotel-duenenmeer-logo.png";
import strandhotelEischlandLogo from "@/assets/strandhotel-eischland-logo.png";
import grandElyseeLogo from "@/assets/grand-elysee-logo.png";
import schmilkaLogo from "@/assets/schmilka-logo.png";
import budersandLogo from "@/assets/budersand-logo.png";
import dasTegernsee from "@/assets/das-tegernsee-logo.png";
import schindelbruchLogo from "@/assets/schindelbruch-logo.svg";
import romantischerWinkelLogo from "@/assets/romantischer-winkel-logo.svg";
import parkhotelBremenLogo from "@/assets/parkhotel-bremen-logo.png";
import rivaBodenseeLogo from "@/assets/riva-bodensee-logo.png";
import laMaisonLogo from "@/assets/la-maison-logo.jpg";
interface Reference {
  name: string;
  domain: string;
  logoUrl?: string; // Optional: direct logo URL
  showName?: boolean; // Optional: show name below logo
  invertColors?: boolean; // Optional: invert colors for white logos on light backgrounds
  category?: string;
}
const references: Reference[] = [{
  name: "Atlantic Hotels",
  domain: "atlantic-hotels.de",
  category: "Hotel",
  showName: false
}, {
  name: "Center Parcs",
  domain: "centerparcs.de",
  logoUrl: centerparcsLogo,
  category: "Resort",
  showName: false
}, {
  name: "Clipper",
  domain: "clipper-group.com",
  logoUrl: clipperLogo,
  category: "Hospitality",
  showName: false
}, {
  name: "Hotel Esplanade Resort & Spa Bad Saarow",
  domain: "hotel-esplanade.de",
  logoUrl: esplanadeLogo,
  category: "Hotel",
  showName: false
}, {
  name: "Meridian Spa",
  domain: "meridianspa.de",
  logoUrl: "https://www.meridianspa.de/fixed-images/meridian-logo-dark-bg.png",
  category: "Spa",
  showName: false
}, {
  name: "DGzRS Seenotretter",
  domain: "seenotretter.de",
  logoUrl: "https://www.seenotretter.de/_assets/9ebe51156f8229bdb6dd8b3e7a310c1f/Static/mandant/img/logo-stage.svg",
  category: "Organisation",
  showName: false
}, {
  name: "Golfhotel Usedom",
  domain: "golfhotel-usedom.de",
  logoUrl: "https://www.golfhotel-usedom.de/templates/balmerseehotelgolfspa/images/logo.svg",
  category: "Hotel",
  showName: false
}, {
  name: "Grand Hotel Heiligendamm",
  domain: "grandhotel-heiligendamm.de",
  logoUrl: "https://www.grandhotel-heiligendamm.de/wp-content/uploads/2024/01/logo_ghh_normal_weiss_rgb.svg",
  category: "Hotel",
  showName: false,
  invertColors: true
}, {
  name: "Deimann Hotel",
  domain: "deimann.de",
  logoUrl: deimannLogo,
  category: "Hotel",
  showName: false
}, {
  name: "Jagdhaus Eiden",
  domain: "jagdhaus-eiden.de",
  logoUrl: jagdhausEidenLogo,
  category: "Hotel",
  showName: false
}, {
  name: "PURS",
  domain: "purs.com",
  logoUrl: "https://purs.com/wp-content/uploads/2023/02/purs-logo-header-black.png.webp",
  category: "Hotel",
  showName: false
}, {
  name: "Strandhotel Dünenmeer",
  domain: "strandhotel-duenenmeer.de",
  logoUrl: strandhotelDuenenmeerLogo,
  category: "Hotel",
  showName: false
}, {
  name: "Strandhotel Eischland",
  domain: "strandhotel-eischland.de",
  logoUrl: strandhotelEischlandLogo,
  category: "Hotel",
  showName: false
}, {
  name: "Grand Elysee Hamburg",
  domain: "grand-elysee.com",
  logoUrl: grandElyseeLogo,
  category: "Hotel",
  showName: false
}, {
  name: "Bio Hotel Kenners LandLust",
  domain: "schmilka.de",
  logoUrl: schmilkaLogo,
  category: "Hotel",
  showName: false
}, {
  name: "Budersand Hotel Golf & Spa Sylt",
  domain: "budersand.de",
  logoUrl: budersandLogo,
  category: "Hotel",
  showName: false
}, {
  name: "Das Tegernsee",
  domain: "dastegernsee.com",
  logoUrl: dasTegernsee,
  category: "Hotel",
  showName: false
}, {
  name: "Schindelbruch",
  domain: "schindelbruch.de",
  logoUrl: schindelbruchLogo,
  category: "Hotel",
  showName: false
}, {
  name: "Romantischer Winkel",
  domain: "romantischer-winkel.de",
  logoUrl: romantischerWinkelLogo,
  category: "Hotel",
  showName: false
}, {
  name: "Parkhotel Bremen",
  domain: "park-hotel-bremen.de",
  logoUrl: parkhotelBremenLogo,
  category: "Hotel",
  showName: false
}, {
  name: "Riva - Das Hotel am Bodensee",
  domain: "hotel-riva.de",
  logoUrl: rivaBodenseeLogo,
  category: "Hotel",
  showName: false
}, {
  name: "La Maison Hotel",
  domain: "lamaison-hotel.de",
  logoUrl: laMaisonLogo,
  category: "Hotel",
  showName: false
}];
export const ReferencesSection = () => {
  const [showAll, setShowAll] = useState(false);
  const [shuffledReferences, setShuffledReferences] = useState<Reference[]>([]);

  // Shuffle references on component mount
  useEffect(() => {
    const shuffleArray = (array: Reference[]) => {
      const newArray = [...array];
      for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
      }
      return newArray;
    };
    setShuffledReferences(shuffleArray(references));
  }, []);

  const displayedReferences = showAll ? shuffledReferences : shuffledReferences.slice(0, 8);

  const toggleShowAll = () => {
    setShowAll(!showAll);
    if (showAll) {
      // Scroll to section when collapsing
      document.getElementById('references-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return <section id="references-section" className="py-16 sm:py-24 bg-gradient-to-b from-background via-background/95 to-background">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <GradientHeading level="h2" size="lg" align="center">
            Referenzen
          </GradientHeading>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto mt-4">Vertrauen Sie auf unsere Erfahrung mit führenden Unternehmen der 
Hotellerie & Gastronomie</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 max-w-6xl mx-auto transition-all duration-500">
          {displayedReferences.map((reference) => (
            <div key={reference.domain} className="group relative bg-card/50 backdrop-blur-sm border border-border/50 rounded-lg p-6 sm:p-8 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 flex flex-col items-center gap-4">
              <CompanyLogo domain={reference.domain} name={reference.name} logoUrl={reference.logoUrl} invertColors={reference.invertColors} className="w-full h-16 sm:h-20" />
              {reference.showName && <p className="text-sm font-medium text-muted-foreground text-center">
                  {reference.name}
                </p>}
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-8 sm:mt-12">
          <Button onClick={toggleShowAll} variant="outline" className="gap-2">
            {showAll ? (
              <>
                Weniger anzeigen
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                Mehr anzeigen
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </section>;
};